import { z } from 'zod';

import type { CommercialService } from './CommercialService';
import { OPTIMIZE_ROUTE_TYPES, type OptimizeRouteType } from './RulesConfig';

export interface ServiceVisitType {
  id: string;
  name?: string;
  [key: string]: unknown;
}

export interface LogisticOperatorRulesConfig {
  strategy?: {
    optimizeRoute?: {
      type?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ServiceWithOperator {
  id: string;
  commercialServiceId: string;
  logisticOperatorId: string;
  serviceVisitTypeId: string;
  apiCode?: string | null;
  serviceVisitType?: ServiceVisitType | null;
  rulesConfig?: LogisticOperatorRulesConfig | null;
  commercialService?: CommercialService | null;
  deliverySlaHours?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClassifiableService {
  serviceVisitType?: { name?: string | null; type?: string | null } | null;
  commercialService?: { name?: string | null; commercialName?: string | null } | null;
}

const visitTypeOf = (
  service?: ClassifiableService | null
): 'PICKUP' | 'DELIVERY' | null => {
  const type = service?.serviceVisitType?.type?.toUpperCase();
  return type === 'PICKUP' || type === 'DELIVERY' ? type : null;
};

/**
 * Nombres con los que se clasifica un servicio. Se mira el tipo de visita y
 * tambien el nombre comercial: hay servicios cuyo tipo de visita no dice de que
 * se trata y solo el nombre comercial ("Retiro Flex") lo aclara.
 */
const classifiableServiceNames = (
  service?: ClassifiableService | null
): string[] =>
  [
    service?.serviceVisitType?.name,
    service?.commercialService?.name,
    service?.commercialService?.commercialName,
  ]
    .filter((name): name is string => typeof name === 'string')
    .map((name) => name.toLowerCase());

export const isPickupServiceVisit = (
  service?: ClassifiableService | null
): boolean => {
  const type = visitTypeOf(service);
  if (type) return type === 'PICKUP';
  return classifiableServiceNames(service).some(
    (name) => name.includes('pickup') || name.includes('retiro')
  );
};

export const isDeliveryServiceVisit = (
  service?: ClassifiableService | null
): boolean => {
  const type = visitTypeOf(service);
  if (type) return type === 'DELIVERY';
  return (
    !isPickupServiceVisit(service) &&
    classifiableServiceNames(service).some(
      (name) => name.includes('delivery') || name.includes('entrega')
    )
  );
};

export const getServiceWithOperatorDisplayName = (
  service?: ServiceWithOperator | null
): string =>
  ((service as { description?: string } | null | undefined)?.description ||
    service?.commercialService?.commercialName ||
    service?.commercialService?.name ||
    service?.serviceVisitType?.name ||
    service?.id ||
    ''
  ).trim();

export interface NewCommercialServicePayload {
  name: string;
  externalId: string;
  description?: string;
  commercialName?: string;
}

export interface CreateServiceWithExistingCommercialPayload {
  commercialServiceId: string;
  serviceVisitTypeId: string;
  rulesConfig?: LogisticOperatorRulesConfig | null;
}

export interface CreateServiceWithNewCommercialPayload {
  newCommercialService: NewCommercialServicePayload;
  serviceVisitTypeId: string;
  rulesConfig?: LogisticOperatorRulesConfig | null;
}

export type NewServiceWithOperator =
  | CreateServiceWithExistingCommercialPayload
  | CreateServiceWithNewCommercialPayload;

export const defaultNewCommercialServicePayload: NewCommercialServicePayload = {
  name: '',
  externalId: '',
  description: '',
  commercialName: '',
};

export interface UpdateRulesConfigOnlyPayload {
  rulesConfig?: LogisticOperatorRulesConfig | null;
  serviceVisitTypeId?: string;
}

export type UpdateServiceWithExistingCommercialPayload =
  UpdateRulesConfigOnlyPayload & {
    commercialServiceId: string;
    newCommercialService?: never;
  };

export type UpdateServiceWithNewCommercialPayload =
  UpdateRulesConfigOnlyPayload & {
    newCommercialService: NewCommercialServicePayload;
    commercialServiceId?: never;
  };

export type UpdateServiceWithOperatorPayload =
  | UpdateRulesConfigOnlyPayload
  | UpdateServiceWithExistingCommercialPayload
  | UpdateServiceWithNewCommercialPayload;

export const newCommercialServicePayloadSchema = z.object({
  name: z.string().min(1, 'El nombre del servicio es requerido'),
  externalId: z.string().min(1, 'El ID externo es requerido'),
  description: z.string().optional(),
  commercialName: z.string().optional(),
});

export const createServiceWithExistingCommercialSchema = z.object({
  commercialServiceId: z
    .string()
    .min(1, 'Debe seleccionar un servicio comercial'),
  serviceVisitTypeId: z
    .string()
    .uuid('Debe seleccionar un tipo de visita válido')
    .min(1, 'El tipo de visita es obligatorio'),
  rulesConfig: z.any().optional(),
});

export const createServiceWithNewCommercialSchema = z.object({
  newCommercialService: newCommercialServicePayloadSchema,
  serviceVisitTypeId: z
    .string()
    .uuid('Debe seleccionar un tipo de visita válido')
    .min(1, 'El tipo de visita es obligatorio'),
  rulesConfig: z.any().optional(),
});

export const createServiceWithOperatorSchema = z.union([
  createServiceWithExistingCommercialSchema,
  createServiceWithNewCommercialSchema,
]);

export const updateServiceWithOperatorSchema = z.union([
  createServiceWithExistingCommercialSchema,
  createServiceWithNewCommercialSchema,
  z
    .object({
      rulesConfig: z.any().optional(),
    })
    .strict(),
]);

export type CreateServiceWithOperatorSchema = z.infer<
  typeof createServiceWithOperatorSchema
>;

export const optimizeRouteTypeLabels: Record<OptimizeRouteType, string> = {
  delivery: 'Delivery',
  pickup: 'Pickup',
  'pickup-delivery': 'Pickup y Delivery',
};

const serviceVisitTypeIdSchema = z
  .string()
  .min(1, 'Debe seleccionar un tipo de visita')
  .uuid('El tipo de visita seleccionado no es válido');

export const createServiceFormSchema = z.object({
  newService: newCommercialServicePayloadSchema,
  serviceVisitTypeId: serviceVisitTypeIdSchema,
  optimizeRouteType: z
    .enum(OPTIMIZE_ROUTE_TYPES)
    .or(z.literal(''))
    .optional()
    .transform((value) => value ?? ''),
});

export type CreateServiceFormValues = z.infer<typeof createServiceFormSchema>;

export const getDefaultCreateServiceFormValues =
  (): CreateServiceFormValues => ({
    newService: { ...defaultNewCommercialServicePayload },
    serviceVisitTypeId: '',
    optimizeRouteType: '',
  });

export const editServiceFormSchema = z.object({
  name: z
    .string()
    .min(1, 'El nombre del servicio es requerido')
    .min(3, 'El nombre debe tener al menos 3 caracteres')
    .max(50, 'El nombre no puede exceder 50 caracteres'),
  externalId: z.string().min(1, 'El ID externo es requerido'),
  commercialName: z.string().optional(),
  description: z.string().optional(),
  serviceVisitTypeId: serviceVisitTypeIdSchema,
  optimizeRouteType: z
    .enum(OPTIMIZE_ROUTE_TYPES)
    .or(z.literal(''))
    .optional()
    .transform((value) => value ?? ''),
});

export type EditServiceFormValues = z.infer<typeof editServiceFormSchema>;

export const getDefaultEditServiceFormValues = (): EditServiceFormValues => ({
  name: '',
  externalId: '',
  commercialName: '',
  description: '',
  serviceVisitTypeId: '',
  optimizeRouteType: '',
});

export const getOptimizeRouteTypeFromRules = (
  rulesConfig?: LogisticOperatorRulesConfig | null
) =>
  typeof rulesConfig?.strategy?.optimizeRoute?.type === 'string'
    ? (rulesConfig.strategy.optimizeRoute.type as string)
    : undefined;
